import type { MqttConnectionStatus, DtcEntry } from "../types/telemetry";
import type { RadioCaptureState } from "../types/radio";
import { useRoute } from "../contexts/RouterContext";
import { formatStintClock, type StintClock } from "../hooks/useStintClock";

interface StatusBarProps {
  connectionStatus: MqttConnectionStatus;
  hasGpsFix: boolean;
  dtcs: DtcEntry[];
  radioState: RadioCaptureState;
  radioVisible?: boolean;
  alertCount?: number;
  telemetryStale?: boolean;
  stint?: StintClock;
  alertsMuted?: boolean;
  onToggleAlertDrawer?: () => void;
  onToggleRadio?: () => void;
  onToggleAlertsMuted?: () => void;
}

const statusIndicator: Record<MqttConnectionStatus, { color: string; label: string; pulse: boolean }> = {
  connected: { color: "var(--green)", label: "MQTT", pulse: false },
  connecting: { color: "var(--yellow)", label: "MQTT ...", pulse: true },
  disconnected: { color: "var(--red)", label: "MQTT OFF", pulse: false },
  error: { color: "var(--red)", label: "MQTT ERR", pulse: true },
};

const radioIndicator: Record<RadioCaptureState, { color: string; label: string; pulse: boolean }> = {
  listening: { color: "var(--green)", label: "RADIO", pulse: true },
  connecting: { color: "var(--yellow)", label: "RADIO ...", pulse: true },
  reconnecting: { color: "var(--yellow)", label: "RADIO ...", pulse: true },
  idle: { color: "var(--text-dim)", label: "RADIO", pulse: false },
  error: { color: "var(--red)", label: "RADIO ERR", pulse: false },
};

export function StatusBar({
  connectionStatus,
  hasGpsFix,
  dtcs,
  radioState,
  radioVisible = true,
  alertCount,
  telemetryStale = false,
  stint,
  alertsMuted = false,
  onToggleAlertDrawer,
  onToggleRadio,
  onToggleAlertsMuted,
}: StatusBarProps) {
  const { navigate } = useRoute();
  const mqttStatus = statusIndicator[connectionStatus] ?? statusIndicator.disconnected;
  const radioStat = radioIndicator[radioState] ?? radioIndicator.idle;
  const showStint = stint !== undefined && stint.state !== "none";

  return (
    <div className="status-bar">
      <div className="status-bar-left">
        <span className="status-item">
          <span
            className={`status-dot${mqttStatus.pulse ? " status-dot--pulse" : ""}`}
            style={{ backgroundColor: mqttStatus.color, color: mqttStatus.color }}
          />
          {mqttStatus.label}
        </span>
        <span className="status-item">
          <span
            className="status-dot"
            style={{
              backgroundColor: hasGpsFix ? "var(--green)" : "var(--text-muted)",
              color: hasGpsFix ? "var(--green)" : "var(--text-muted)",
            }}
          />
          {hasGpsFix ? "GPS" : "NO GPS"}
        </span>
        {telemetryStale && (
          <span className="status-item telemetry-stale-alert" role="alert">
            <span
              className="status-dot status-dot--pulse"
              style={{ backgroundColor: "var(--yellow)", color: "var(--yellow)" }}
            />
            DATA STALE
          </span>
        )}
      </div>

      <div className="status-bar-center">
        <h1 className="app-title">VTMS</h1>
      </div>

      <div className="status-bar-right">
        {onToggleRadio && (
          <button
            className={`status-icon-btn ${radioVisible ? "active" : ""}`}
            onClick={onToggleRadio}
            aria-label="Toggle Radio"
            title={`Radio — ${radioStat.label}`}
            type="button"
            aria-pressed={radioVisible}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M5 10a7 7 0 0 0 14 0" />
              <line x1="12" y1="17" x2="12" y2="22" />
              <line x1="8" y1="22" x2="16" y2="22" />
            </svg>
            <span
              className={`status-icon-btn__dot ${radioStat.pulse ? "status-dot--pulse" : ""}`}
              style={{ backgroundColor: radioStat.color, color: radioStat.color }}
            />
          </button>
        )}
        {dtcs.length > 0 && (
          <span className="status-item dtc-alert">
            <span
              className="status-dot status-dot--pulse"
              style={{ backgroundColor: "var(--red)", color: "var(--red)" }}
            />
            {dtcs.length} DTC{dtcs.length !== 1 ? "s" : ""}:{" "}
            {dtcs.map((d) => d.code).join(", ")}
          </span>
        )}
        {showStint && (
          <span
            className={`status-item status-stint status-stint--${stint.state}`}
            title="Time since stint start (last refuel or session start)"
            data-testid="status-stint"
          >
            STINT {formatStintClock(stint.elapsedMs)}
          </span>
        )}
        {onToggleAlertsMuted && (
          <button
            className={`status-icon-btn${alertsMuted ? " status-icon-btn--muted" : ""}`}
            onClick={onToggleAlertsMuted}
            aria-label={alertsMuted ? "Unmute alert sounds" : "Mute alert sounds"}
            aria-pressed={alertsMuted}
            title={alertsMuted ? "Alert sounds muted" : "Alert sounds on"}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              {alertsMuted ? (
                <>
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </>
              ) : (
                <>
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </>
              )}
            </svg>
          </button>
        )}
        {onToggleAlertDrawer && (
          <button
            className="status-icon-btn"
            onClick={onToggleAlertDrawer}
            aria-label="Toggle alert drawer"
            title="Alert History"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {(alertCount ?? 0) > 0 && (
              <span className="status-icon-btn__badge">{alertCount}</span>
            )}
          </button>
        )}
        <button
          className="status-pitboard-btn"
          onClick={() => navigate({ view: "pitboard" })}
          aria-label="Pit board mode"
          title="Pit Board"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        </button>
      </div>
    </div>
  );
}
